import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Row,
  Col,
  Empty,
  InputNumber,
  Form,
  Tag,
  Select,
  DatePicker,
  Spin,
  Table
} from 'antd';
import {
  DownloadOutlined,
  LoadingOutlined,
  FileTextOutlined,
  WarningOutlined
} from '@ant-design/icons';
import {
  getStatusFromRecord,
  STATUS_CONFIG
} from '@/features/sieveLabel/components/SieveAllocation/SieveAllocation.helper';
import useSieveAccess from '@/features/sieveLabel/hooks/useSieveAccess';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Layout from '@/common/Layout';
import CustomTable from '@/common/Content/CustomTable';
import CustomModal from '@/common/CustomModal';
import {
  createSieveLabel,
  getSieveLabels,
  regenerateMissingSievePdfs,
  generateSieveUsageLogReport
} from '@/features/sieveLabel/redux/slice';
import styles from './index.module.less';

const buildBlobPdfUrl = (rawUrl) => {
  if (!rawUrl) return '';
  const filename = String(rawUrl).split(/[?#]/)[0].split('/').pop();
  if (!filename) return '';
  return `${process.env.REACT_APP_STORAGE_URL}${filename}`;
};

const ITEM_TYPE_OPTIONS = [
  { value: 'Sieve', label: 'Sieve' },
  { value: 'Screen', label: 'Screen' }
];

const SieveLabelGeneration = () => {
  const dispatch = useDispatch();
  const { list, listPagination, loading, pendingCount, stalePendingCount } =
    useSelector((state) => state.sieveLabel);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [rowErrors, setRowErrors] = useState({});

  // 2-step create modal state
  const [createStep, setCreateStep] = useState('count'); // 'count' | 'items'
  const [createOpen, setCreateOpen] = useState(false);
  const [countForm] = Form.useForm();
  const [itemRows, setItemRows] = useState([]); // [{item_type, inhouse_number}]

  const { canAct } = useSieveAccess();
  const allRowsValid = itemRows.every(
    (r) => r.inhouse_number?.trim().length > 0
  );

  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logReportForm] = Form.useForm();
  const [fromDateValue, setFromDateValue] = useState(null);
  const [reportGenerating, setReportGenerating] = useState(false);

  const pollRef = useRef(null);
  const fetchListRef = useRef(null);

  useEffect(() => {
    fetchList();
  }, [page, perPage, search]);

  useEffect(() => {
    if (pendingCount > 0) {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => fetchListRef.current(), 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current && pendingCount === 0) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [pendingCount]);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    []
  );

  const fetchList = () => {
    const payload = { page, per_page: perPage };
    if (search) payload.search = search;
    dispatch(getSieveLabels(payload));
  };
  fetchListRef.current = fetchList;

  const openCreate = () => {
    countForm.setFieldsValue({ count: 1 });
    setCreateStep('count');
    setItemRows([]);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (submitting) return;
    setCreateOpen(false);
    setCreateStep('count');
    setItemRows([]);
    setRowErrors({});
    countForm.resetFields();
  };

  const handleCountNext = ({ count }) => {
    const n = Number(count) || 1;
    const rows = Array.from({ length: n }, () => ({
      item_type: 'Sieve',
      inhouse_number: ''
    }));
    setItemRows(rows);
    setCreateStep('items');
  };

  const updateRow = (index, field, value) => {
    setItemRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    if (field === 'inhouse_number') {
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const handleCreate = async () => {
    if (submitting) return;
    const errors = {};
    itemRows.forEach((r, i) => {
      if (!r.inhouse_number?.trim()) errors[i] = 'In-house number is required';
    });
    if (Object.keys(errors).length > 0) {
      setRowErrors(errors);
      return;
    }
    const items = itemRows.map((r) => ({
      item_type: r.item_type || 'Sieve',
      inhouse_number: r.inhouse_number.trim()
    }));
    setSubmitting(true);
    try {
      const res = await dispatch(createSieveLabel({ items }));
      if (res?.payload?.success) {
        closeCreate();
        setPage(1);
        fetchList();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogReport = async (values) => {
    setReportGenerating(true);
    try {
      const from_date = values.from_date.format('YYYY-MM-DD');
      const to_date = values.to_date.format('YYYY-MM-DD');
      const res = await dispatch(
        generateSieveUsageLogReport({ from_date, to_date, flat: true })
      );
      if (res?.payload?.success) {
        setLogModalOpen(false);
        logReportForm.resetFields();
        setFromDateValue(null);
      }
    } finally {
      setReportGenerating(false);
    }
  };

  const handleRegenerateMissing = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      await dispatch(regenerateMissingSievePdfs());
      fetchList();
    } finally {
      setRegenerating(false);
    }
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 70,
      render: (_, __, idx) => (page - 1) * perPage + idx + 1
    },
    {
      title: 'Sieve/Screen Label',
      dataIndex: 'sieve_label_number',
      key: 'sieve_label_number',
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{v}</span>
    },
    {
      title: 'Type',
      dataIndex: 'item_type',
      key: 'item_type',
      width: 100,
      render: (v) => (
        <Tag color={v === 'Screen' ? 'purple' : 'blue'}>{v || 'Sieve'}</Tag>
      )
    },
    {
      title: 'In-house No.',
      dataIndex: 'inhouse_number',
      key: 'inhouse_number',
      render: (v) => v || '—'
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const s = getStatusFromRecord(record);
        const cfg = STATUS_CONFIG[s];
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      }
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      render: (v) => v || '—'
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (v) =>
        v ? moment(v).utcOffset('+05:30').format('DD MMM YYYY HH:mm') : '—'
    },
    {
      title: 'Label',
      dataIndex: 'pdf_url',
      key: 'pdf_url',
      width: 100,
      render: (v, record) => {
        if (v) {
          return (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              href={buildBlobPdfUrl(v)}
              target="_blank"
              rel="noopener noreferrer"
              download={`${record.sieve_label_number}.pdf`}
              style={{ padding: 0 }}
            >
              PDF
            </Button>
          );
        }
        const isStale = new Date() - new Date(record.createdAt) > 90 * 1000;
        return isStale ? (
          <WarningOutlined
            style={{ color: '#faad14', cursor: 'pointer' }}
            title="PDF stalled — click to retry"
            onClick={handleRegenerateMissing}
          />
        ) : (
          <Spin size="small" />
        );
      }
    }
  ];

  // Item table columns for the 2-step create modal
  const itemTableColumns = [
    {
      title: '#',
      key: 'idx',
      width: 50,
      render: (_, __, i) => i + 1
    },
    {
      title: 'Type',
      key: 'item_type',
      width: 140,
      render: (_, row, i) => (
        <Select
          value={row.item_type}
          options={ITEM_TYPE_OPTIONS}
          onChange={(v) => updateRow(i, 'item_type', v)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'In-house Number',
      key: 'inhouse_number',
      render: (_, row, i) => (
        <div>
          <Input
            value={row.inhouse_number}
            placeholder="e.g. SV-IH-001"
            onChange={(e) => updateRow(i, 'inhouse_number', e.target.value)}
            status={rowErrors[i] ? 'error' : ''}
          />
          {rowErrors[i] && (
            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 2 }}>
              {rowErrors[i]}
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <Layout sider={false}>
      <div className={styles.page_wrapper}>
        <div className={styles.page_header}>
          <div className={styles.title_block}>
            <h2 className={styles.page_title}>
              Sieve / Screen Label Generation
            </h2>
            {pendingCount > 0 && (
              <span
                className={
                  stalePendingCount > 0
                    ? styles.pending_chip_warn
                    : styles.pending_chip
                }
                title={
                  stalePendingCount > 0
                    ? 'One or more labels may have stalled'
                    : 'Labels are generating in the background'
                }
              >
                {stalePendingCount > 0 ? (
                  <WarningOutlined />
                ) : (
                  <LoadingOutlined spin />
                )}{' '}
                {pendingCount}{' '}
                {stalePendingCount > 0 ? 'may have stalled' : 'generating'}
                {stalePendingCount > 0 && (
                  <Button
                    type="link"
                    size="small"
                    onClick={handleRegenerateMissing}
                    loading={regenerating}
                    disabled={regenerating}
                    className={styles.pending_retry}
                  >
                    Retry
                  </Button>
                )}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              icon={<FileTextOutlined />}
              disabled={!canAct}
              onClick={() => {
                logReportForm.resetFields();
                setFromDateValue(null);
                setLogModalOpen(true);
              }}
            >
              Activity Log
            </Button>
            <Button
              type="primary"
              onClick={openCreate}
              disabled={submitting || !canAct}
            >
              Create Sieve/Screen Labels
            </Button>
          </div>
        </div>

        <Row gutter={16} className={styles.search_row}>
          <Col xs={24} md={10} lg={8}>
            <Input.Search
              placeholder="Search by sieve/screen label number"
              allowClear
              onSearch={(v) => {
                setPage(1);
                setSearch(v);
              }}
            />
          </Col>
        </Row>

        <CustomTable
          columns={columns}
          data={list}
          rowKey={(r) => r.id}
          loading={loading}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty description="No Sieve/Screen labels found. Create one to get started." />
            )
          }}
          pagination={{
            total: listPagination?.total_records || 0,
            pageSize: perPage,
            current: page,
            onChange: (p, ps) => {
              setPage(p);
              setPerPage(ps);
            }
          }}
          showPaginationMeta
        />

        {/* Step 1: Enter count */}
        <CustomModal
          title="Create Sieve/Screen Labels — Step 1 of 2"
          visible={createOpen && createStep === 'count'}
          onCancel={closeCreate}
          customFooter={false}
          footer={null}
          width={420}
        >
          <p style={{ color: '#595959', marginBottom: 16 }}>
            How many labels do you want to create? You will specify the type and
            in-house number for each item in the next step.
          </p>
          <Form
            form={countForm}
            layout="vertical"
            initialValues={{ count: 1 }}
            onFinish={handleCountNext}
          >
            <Form.Item
              label="Number of labels to create"
              name="count"
              rules={[
                { required: true, message: 'Required' },
                {
                  type: 'number',
                  min: 1,
                  max: 100,
                  message: 'Enter a number between 1 and 100'
                }
              ]}
            >
              <InputNumber
                min={1}
                max={100}
                style={{ width: '100%' }}
                placeholder="e.g. 5"
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={closeCreate}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{ marginLeft: 8 }}
              >
                Next →
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Step 2: Item details table */}
        <CustomModal
          title={`Create Sieve/Screen Labels — Step 2 of 2 (${
            itemRows.length
          } item${itemRows.length !== 1 ? 's' : ''})`}
          visible={createOpen && createStep === 'items'}
          onCancel={closeCreate}
          customFooter={false}
          footer={null}
          width={600}
        >
          <p style={{ color: '#595959', marginBottom: 12 }}>
            Set the type and in-house number for each item. All items will
            receive sequential sieve labels. In-house number is required.
          </p>
          <Table
            columns={itemTableColumns}
            dataSource={itemRows}
            rowKey={(_, i) => i}
            pagination={false}
            size="small"
            scroll={{ y: 300 }}
          />
          <div
            style={{
              textAlign: 'right',
              marginTop: 16,
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <Button
              onClick={() => {
                setCreateStep('count');
                setRowErrors({});
              }}
              disabled={submitting}
            >
              ← Back
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={closeCreate} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleCreate}
                loading={submitting}
                disabled={submitting || !allRowsValid}
              >
                Create {itemRows.length} Label{itemRows.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </CustomModal>

        {/* Activity Log Report modal */}
        <CustomModal
          title="Sieve/Screen Activity Log Report"
          visible={logModalOpen}
          onCancel={() => {
            if (!reportGenerating) {
              setLogModalOpen(false);
              logReportForm.resetFields();
              setFromDateValue(null);
            }
          }}
          customFooter={false}
          footer={null}
          width={440}
        >
          <Form
            form={logReportForm}
            layout="vertical"
            onFinish={handleLogReport}
          >
            <Form.Item
              label="From Date"
              name="from_date"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                onChange={(v) => setFromDateValue(v)}
              />
            </Form.Item>
            <Form.Item
              label="To Date"
              name="to_date"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(d) =>
                  fromDateValue && d.isBefore(fromDateValue, 'day')
                }
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button
                onClick={() => {
                  setLogModalOpen(false);
                  logReportForm.resetFields();
                  setFromDateValue(null);
                }}
                disabled={reportGenerating}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={reportGenerating}
                disabled={reportGenerating}
                style={{ marginLeft: 8 }}
              >
                Generate PDF
              </Button>
            </div>
          </Form>
        </CustomModal>
      </div>
    </Layout>
  );
};

export default SieveLabelGeneration;
